/* ============================================================
 * AYU Demo-Registrierung – Gate vor /demo
 * Sammelt: Vorname, Nachname, E-Mail, Telefon → leads (source="demo")
 * Nach Erfolg: Redirect zu /api/demo/start
 * Design: Konsistent mit AYU Sacred Modern
 * ============================================================ */
import { useState, useEffect, useRef } from "react";
import { IMAGES } from "@/lib/constants";
import { trpc } from "@/lib/trpc";
import { useI18n } from "@/contexts/I18nContext";
import {
  User, Phone, Mail, ArrowRight, Shield, CheckCircle, Play, Loader2
} from "lucide-react";

/* ── Phone number formatting & validation ── */
function formatPhoneNumber(value: string): string {
  const cleaned = value.replace(/[^\d+]/g, "");
  if (cleaned.startsWith("+49")) {
    const digits = cleaned.slice(3);
    if (digits.length <= 3) return `+49 ${digits}`;
    if (digits.length <= 6) return `+49 ${digits.slice(0, 3)} ${digits.slice(3)}`;
    if (digits.length <= 10) return `+49 ${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
    return `+49 ${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 10)}`;
  }
  if (cleaned.startsWith("0")) {
    const digits = cleaned.slice(1);
    if (digits.length <= 3) return `+49 ${digits}`;
    if (digits.length <= 6) return `+49 ${digits.slice(0, 3)} ${digits.slice(3)}`;
    if (digits.length <= 10) return `+49 ${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
    return `+49 ${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 10)}`;
  }
  if (cleaned.startsWith("+")) return cleaned;
  if (cleaned.length > 0) {
    const digits = cleaned;
    if (digits.length <= 3) return `+49 ${digits}`;
    if (digits.length <= 6) return `+49 ${digits.slice(0, 3)} ${digits.slice(3)}`;
    if (digits.length <= 10) return `+49 ${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
    return `+49 ${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 10)}`;
  }
  return value;
}

function isValidPhone(phone: string): boolean {
  const cleaned = phone.replace(/[^\d+]/g, "");
  if (!cleaned.startsWith("+")) return false;
  const digits = cleaned.replace(/\D/g, "");
  return digits.length >= 10 && digits.length <= 15;
}

export default function DemoRegistration() {
  const { t } = useI18n();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState<{
    firstName?: string;
    lastName?: string;
    phone?: string;
    email?: string;
  }>({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const redirectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const registerMutation = trpc.leads.registerDemo.useMutation({
    onSuccess: () => {
      setSubmitted(true);
      // Redirect to actual demo after short delay
      redirectTimerRef.current = setTimeout(() => {
        window.location.href = "/api/demo/start";
      }, 2000);
    },
    onError: (err: { message?: string }) => {
      setSubmitting(false);
      setErrors({ email: err.message || t("demoRegistration.errorGeneral") });
    },
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    return () => {
      if (redirectTimerRef.current) clearTimeout(redirectTimerRef.current);
    };
  }, []);

  function handlePhoneChange(value: string) {
    const raw = value.replace(/[^\d+]/g, "");
    if (raw.length > 0) {
      setPhone(formatPhoneNumber(value));
    } else {
      setPhone(value);
    }
    setErrors((prev) => ({ ...prev, phone: undefined }));
  }

  function validate(): boolean {
    const newErrors: typeof errors = {};
    if (!firstName.trim()) newErrors.firstName = t("demoRegistration.errorFirstName");
    if (!lastName.trim()) newErrors.lastName = t("demoRegistration.errorLastName");
    if (!phone.trim()) {
      newErrors.phone = t("demoRegistration.errorPhoneRequired");
    } else if (!isValidPhone(phone.trim())) {
      newErrors.phone = t("demoRegistration.errorPhoneInvalid");
    }
    if (!email.trim()) {
      newErrors.email = t("demoRegistration.errorEmailRequired");
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      newErrors.email = t("demoRegistration.errorEmailInvalid");
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    registerMutation.mutate({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      phone: phone.trim(),
      email: email.trim(),
    });
  }

  return (
    <div
      className="min-h-screen relative overflow-hidden"
      style={{ background: "oklch(0.97 0.005 80)" }}
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-[0.03]">
        <img src={IMAGES.mandala} alt="" className="w-full h-full object-cover" />
      </div>

      {/* Navigation */}
      <nav className="relative z-20 py-5">
        <div className="container flex items-center justify-between">
          <a href="/" className="flex items-center gap-3">
            <img src={IMAGES.logo} alt="AYU" className="h-10 w-auto" />
          </a>
          <a
            href="/"
            className="text-sm text-[oklch(0.5_0.02_80)] hover:text-[oklch(0.52_0.09_128)] transition-colors"
            style={{ fontFamily: "var(--font-body)" }}
          >
            {t("demoRegistration.navBack")}
          </a>
        </div>
      </nav>

      {/* Main Content */}
      <div className="container relative z-10 pb-20">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center min-h-[calc(100vh-100px)]">
          {/* Left: Messaging */}
          <div className="pt-8 lg:pt-0">
            <div className="inline-flex items-center gap-2 mb-6 px-4 py-2 rounded-full border border-[oklch(0.65_0.15_55/0.3)] bg-[oklch(0.65_0.15_55/0.05)]">
              <Play size={14} className="text-[oklch(0.65_0.15_55)]" />
              <span
                className="text-sm font-medium text-[oklch(0.65_0.15_55)]"
                style={{ fontFamily: "var(--font-body)" }}
              >
                {t("demoRegistration.heroBadge")}
              </span>
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[oklch(0.2_0.02_80)] leading-[1.15] mb-6">
              {t("demoRegistration.heroTitle1")}{" "}
              <span className="italic text-[oklch(0.65_0.15_55)]">
                {t("demoRegistration.heroTitle2")}
              </span>
            </h1>

            <p
              className="text-lg text-[oklch(0.45_0.02_80)] leading-relaxed mb-10 max-w-lg"
              style={{ fontFamily: "var(--font-body)" }}
            >
              {t("demoRegistration.heroDesc")}
            </p>

            {/* Trust Badges */}
            <div className="space-y-4">
              {[
                { icon: Play, text: t("demoRegistration.trustBadge1") },
                { icon: Shield, text: t("demoRegistration.trustBadge2") },
                { icon: CheckCircle, text: t("demoRegistration.trustBadge3") },
              ].map((badge, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-[oklch(0.52_0.09_128/0.08)] flex items-center justify-center flex-shrink-0">
                    <badge.icon size={16} className="text-[oklch(0.52_0.09_128)]" />
                  </div>
                  <span
                    className="text-[oklch(0.4_0.02_80)] text-[15px]"
                    style={{ fontFamily: "var(--font-body)" }}
                  >
                    {badge.text}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Form */}
          <div className="flex justify-center lg:justify-end">
            {submitted ? (
              /* Success State */
              <div className="w-full max-w-lg p-10 rounded-3xl bg-white shadow-xl border border-[oklch(0.65_0.15_55/0.15)] text-center">
                <div className="w-20 h-20 rounded-full bg-[oklch(0.65_0.15_55/0.1)] flex items-center justify-center mx-auto mb-6">
                  <CheckCircle size={40} className="text-[oklch(0.65_0.15_55)]" />
                </div>
                <h2 className="text-2xl font-bold text-[oklch(0.2_0.02_80)] mb-3">
                  {t("demoRegistration.successTitle")}
                </h2>
                <p
                  className="text-[oklch(0.45_0.02_80)] mb-4"
                  style={{ fontFamily: "var(--font-body)" }}
                >
                  {t("demoRegistration.successDesc")}
                </p>
                <div className="flex items-center justify-center gap-2 text-[oklch(0.65_0.15_55)]">
                  <Loader2 size={18} className="animate-spin" />
                  <span className="text-sm" style={{ fontFamily: "var(--font-body)" }}>
                    {t("demoRegistration.successLoading")}
                  </span>
                </div>
              </div>
            ) : (
              /* Form */
              <form
                onSubmit={handleSubmit}
                className="w-full max-w-lg p-6 sm:p-10 rounded-3xl bg-white shadow-xl border border-[oklch(0.9_0.01_80)]"
              >
                <h2 className="text-2xl font-bold text-[oklch(0.2_0.02_80)] mb-2">
                  {t("demoRegistration.formTitle")}
                </h2>
                <p
                  className="text-[oklch(0.5_0.02_80)] text-sm mb-8"
                  style={{ fontFamily: "var(--font-body)" }}
                >
                  {t("demoRegistration.formSubtitle")}
                </p>

                {/* Vorname */}
                <div className="mb-5">
                  <label
                    className="block text-sm font-medium text-[oklch(0.3_0.02_80)] mb-2"
                    style={{ fontFamily: "var(--font-body)" }}
                  >
                    {t("demoRegistration.labelFirstName")}
                  </label>
                  <div className="relative">
                    <User
                      size={18}
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-[oklch(0.6_0.02_80)]"
                    />
                    <input
                      type="text"
                      value={firstName}
                      onChange={(e) => {
                        setFirstName(e.target.value);
                        setErrors((prev) => ({ ...prev, firstName: undefined }));
                      }}
                      placeholder={t("demoRegistration.placeholderFirstName")}
                      className={`w-full pl-12 pr-4 py-3.5 rounded-xl border text-[oklch(0.2_0.02_80)] placeholder:text-[oklch(0.65_0.02_80)] transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[oklch(0.52_0.09_128/0.3)] focus:border-[oklch(0.52_0.09_128)] ${
                        errors.firstName
                          ? "border-red-400 bg-red-50/50"
                          : "border-[oklch(0.88_0.02_80)] bg-[oklch(0.98_0.005_80)]"
                      }`}
                      style={{ fontFamily: "var(--font-body)" }}
                    />
                  </div>
                  {errors.firstName && (
                    <p className="text-red-500 text-xs mt-1.5" style={{ fontFamily: "var(--font-body)" }}>
                      {errors.firstName}
                    </p>
                  )}
                </div>

                {/* Nachname */}
                <div className="mb-5">
                  <label
                    className="block text-sm font-medium text-[oklch(0.3_0.02_80)] mb-2"
                    style={{ fontFamily: "var(--font-body)" }}
                  >
                    {t("demoRegistration.labelLastName")}
                  </label>
                  <div className="relative">
                    <User
                      size={18}
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-[oklch(0.6_0.02_80)]"
                    />
                    <input
                      type="text"
                      value={lastName}
                      onChange={(e) => {
                        setLastName(e.target.value);
                        setErrors((prev) => ({ ...prev, lastName: undefined }));
                      }}
                      placeholder={t("demoRegistration.placeholderLastName")}
                      className={`w-full pl-12 pr-4 py-3.5 rounded-xl border text-[oklch(0.2_0.02_80)] placeholder:text-[oklch(0.65_0.02_80)] transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[oklch(0.52_0.09_128/0.3)] focus:border-[oklch(0.52_0.09_128)] ${
                        errors.lastName
                          ? "border-red-400 bg-red-50/50"
                          : "border-[oklch(0.88_0.02_80)] bg-[oklch(0.98_0.005_80)]"
                      }`}
                      style={{ fontFamily: "var(--font-body)" }}
                    />
                  </div>
                  {errors.lastName && (
                    <p className="text-red-500 text-xs mt-1.5" style={{ fontFamily: "var(--font-body)" }}>
                      {errors.lastName}
                    </p>
                  )}
                </div>

                {/* E-Mail */}
                <div className="mb-5">
                  <label
                    className="block text-sm font-medium text-[oklch(0.3_0.02_80)] mb-2"
                    style={{ fontFamily: "var(--font-body)" }}
                  >
                    {t("demoRegistration.labelEmail")}
                  </label>
                  <div className="relative">
                    <Mail
                      size={18}
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-[oklch(0.6_0.02_80)]"
                    />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setErrors((prev) => ({ ...prev, email: undefined }));
                      }}
                      placeholder={t("demoRegistration.placeholderEmail")}
                      className={`w-full pl-12 pr-4 py-3.5 rounded-xl border text-[oklch(0.2_0.02_80)] placeholder:text-[oklch(0.65_0.02_80)] transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[oklch(0.52_0.09_128/0.3)] focus:border-[oklch(0.52_0.09_128)] ${
                        errors.email
                          ? "border-red-400 bg-red-50/50"
                          : "border-[oklch(0.88_0.02_80)] bg-[oklch(0.98_0.005_80)]"
                      }`}
                      style={{ fontFamily: "var(--font-body)" }}
                    />
                  </div>
                  {errors.email && (
                    <p className="text-red-500 text-xs mt-1.5" style={{ fontFamily: "var(--font-body)" }}>
                      {errors.email}
                    </p>
                  )}
                </div>

                {/* Telefon */}
                <div className="mb-8">
                  <label
                    className="block text-sm font-medium text-[oklch(0.3_0.02_80)] mb-2"
                    style={{ fontFamily: "var(--font-body)" }}
                  >
                    {t("demoRegistration.labelPhone")}
                  </label>
                  <div className="relative">
                    <Phone
                      size={18}
                      className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-[oklch(0.6_0.02_80)]"
                    />
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => handlePhoneChange(e.target.value)}
                      placeholder={t("demoRegistration.placeholderPhone")}
                      className={`w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-3.5 rounded-xl border text-sm sm:text-base text-[oklch(0.2_0.02_80)] placeholder:text-[oklch(0.65_0.02_80)] transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[oklch(0.52_0.09_128/0.3)] focus:border-[oklch(0.52_0.09_128)] ${
                        errors.phone
                          ? "border-red-400 bg-red-50/50"
                          : "border-[oklch(0.88_0.02_80)] bg-[oklch(0.98_0.005_80)]"
                      }`}
                      style={{ fontFamily: "var(--font-body)", letterSpacing: "0.02em" }}
                    />
                  </div>
                  {errors.phone && (
                    <p className="text-red-500 text-xs mt-1.5" style={{ fontFamily: "var(--font-body)" }}>
                      {errors.phone}
                    </p>
                  )}
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full flex items-center justify-center gap-2 px-8 py-4 rounded-xl text-white font-semibold transition-all duration-300 hover:scale-[1.02] hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100"
                  style={{
                    background: "oklch(0.65 0.15 55)",
                    fontFamily: "var(--font-body)",
                  }}
                >
                  {submitting ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      {t("demoRegistration.submitLoading")}
                    </>
                  ) : (
                    <>
                      {t("demoRegistration.submitButton")} <ArrowRight size={18} />
                    </>
                  )}
                </button>

                {/* Privacy Note */}
                <p
                  className="text-xs text-[oklch(0.6_0.02_80)] text-center mt-5 leading-relaxed"
                  style={{ fontFamily: "var(--font-body)" }}
                >
                  {t("demoRegistration.privacyText")}{" "}
                  <a
                    href="/datenschutz"
                    className="text-[oklch(0.52_0.09_128)] hover:underline"
                  >
                    {t("demoRegistration.privacyLink")}
                  </a>{" "}
                  {t("demoRegistration.privacyAfter")}
                </p>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="relative z-10 py-6 border-t border-[oklch(0.9_0.01_80)]">
        <div className="container flex flex-col sm:flex-row items-center justify-between gap-3">
          <span
            className="text-xs text-[oklch(0.5_0.02_80)]"
            style={{ fontFamily: "var(--font-body)" }}
          >
            &copy; {new Date().getFullYear()} AYU Healthtech LIMITED
          </span>
          <div className="flex items-center gap-6">
            <a
              href="/impressum"
              className="text-xs text-[oklch(0.5_0.02_80)] hover:text-[oklch(0.52_0.09_128)] transition-colors"
              style={{ fontFamily: "var(--font-body)" }}
            >
              {t("demoRegistration.footerImpressum")}
            </a>
            <a
              href="/datenschutz"
              className="text-xs text-[oklch(0.5_0.02_80)] hover:text-[oklch(0.52_0.09_128)] transition-colors"
              style={{ fontFamily: "var(--font-body)" }}
            >
              {t("demoRegistration.footerDatenschutz")}
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
